import React, { useEffect, useRef, useState } from 'react';

/**
 * Beauty of Freedom — autoplaying, muted, looped YouTube hero video used on
 * the marketing index and on the signed-in Home page.
 *
 * Why this isn't a one-line `<iframe>`:
 *
 *   1. **Browser autoplay policies.** Chromium/WebKit increasingly drop
 *      `autoplay=1` on cold loads even when `mute=1` is set, leaving the
 *      embed parked on the YouTube poster with a center play button.
 *      Enabling the IFrame JS API (`enablejsapi=1`) and explicitly
 *      `postMessage`-ing `playVideo` after the iframe boots is what reliably
 *      kicks playback in those cases. This is the same pattern
 *      `DeferaLoopVideo` uses for the (working) Defera promo loop.
 *
 *   2. **iOS Safari** refuses inline playback without `playsinline=1` and
 *      will simply show the poster otherwise.
 *
 *   3. **Slow / ad-blocked networks.** A poster painted on the wrapper means
 *      the surface is never an empty black box while the YouTube player JS
 *      bootstraps (or while a tracker-blocker briefly delays the
 *      `youtube-nocookie.com` host).
 *
 *   4. **The `loop=1` URL param** is unreliable on long sessions, so we also
 *      listen for `onStateChange === 0` (ended) and force a replay.
 *
 * Controls remain visible (`controls=1`) — this is a 1-minute cinematic the
 * user is meant to be able to pause / replay, not a silent decorative loop.
 *
 *   5. **Page weight.** The YouTube player is about a megabyte of script in
 *      its own frame, and this sits several screens down the landing page, so
 *      the iframe mounts only once the visitor scrolls near it; the poster
 *      holds its place until then, and playback starts as it arrives.
 */
const BEAUTY_VIDEO_ID = 'kx-ZON-hOvw';

export default function BeautyOfFreedomVideo() {
  const iframeRef = useRef(null);
  const wrapperRef = useRef(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (near || !wrapperRef.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        setNear(true);
      }
    }, { rootMargin: '400px 0px' });
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [near]);

  useEffect(() => {
    if (!near) return undefined;
    const send = (func, args = []) => {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func, args }),
          '*'
        );
      } catch (_) {
        /* no-op */
      }
    };

    // Subscribe to player events. Sent twice at different delays because the
    // YouTube player JS bootstraps at very different speeds depending on
    // network / ad-blocker state.
    const subscribe = () => {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: 'listening', id: BEAUTY_VIDEO_ID }),
          '*'
        );
      } catch (_) {
        /* no-op */
      }
    };

    // Force-play attempts. `mute=1` already in URL satisfies the muted
    // autoplay path, but the actual play call sometimes only lands after the
    // player has finished its handshake — hence the staggered retries.
    const playAttempts = [400, 1200, 2500].map((delay) =>
      setTimeout(() => {
        subscribe();
        send('mute');
        send('playVideo');
      }, delay)
    );

    const onMessage = (e) => {
      let origin = '';
      try {
        origin = new URL(e.origin || '').hostname;
      } catch (_) {
        return;
      }
      if (!/youtube(-nocookie)?\.com$/.test(origin)) return;

      let data = e.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (_) {
          return;
        }
      }
      // Player state 0 = ended → force a replay so the built-in `loop` param
      // doesn't silently drop after long idle sessions.
      if (data?.event === 'onStateChange' && data?.info === 0) {
        send('seekTo', [0, true]);
        send('playVideo');
      }
    };

    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
      playAttempts.forEach(clearTimeout);
    };
  }, [near]);

  const src =
    `https://www.youtube-nocookie.com/embed/${BEAUTY_VIDEO_ID}` +
    `?autoplay=1&mute=1&loop=1&playlist=${BEAUTY_VIDEO_ID}` +
    `&controls=1&modestbranding=1&rel=0&iv_load_policy=3&fs=1` +
    `&playsinline=1&enablejsapi=1&color=white`;

  const poster = `https://i.ytimg.com/vi/${BEAUTY_VIDEO_ID}/maxresdefault.jpg`;

  return (
    <div
      ref={wrapperRef}
      className="relative aspect-video bg-center bg-cover"
      style={{ backgroundImage: `url(${poster})` }}
    >
      {near && (
      <iframe
        ref={iframeRef}
        className="absolute inset-0 w-full h-full"
        src={src}
        title="The Beauty of Freedom"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="eager"
        referrerPolicy="strict-origin-when-cross-origin"
      />
      )}
    </div>
  );
}
